import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import EditingReturnBanner, { backToLabelFor } from '../projects/EditingReturnBanner'
import { useFieldFocusHandler } from '../../hooks/useFieldFocusHandler'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import PageHeader from '../layout/PageHeader'
import MultiSelect from '../ui/MultiSelect'
import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import { useCatalog } from '../../contexts/CatalogContext'
import { DEFAULT_CLEANING_GRINDING_NOTES } from '../../constants/defaultCleaningGrindingNotes'

function resolveCleaningNotes(raw: unknown): string {
  return typeof raw === 'string' && raw.trim().length > 0 ? raw : DEFAULT_CLEANING_GRINDING_NOTES
}

const EMPTY_POSTWELD_PAYLOAD = JSON.stringify({
  postHeating: [] as string[],
  pwht: [] as string[],
  coolingMethod: [] as string[],
  notes: '',
  cleaningNotes: DEFAULT_CLEANING_GRINDING_NOTES,
  rootExternalTreatment: ''
})

export default function WpsPostWeld() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  // Slice 09 of pqr-preview-field-navigation. Entry points:
  //   - "Cleaning / Grinding" notes row    -> ?focusField=cleaningNotes
  //   - "Root External Treatment" row      -> ?focusField=rootExternalTreatment
  // Resolved by `[data-focus-field="<key>"]`, scrolled-into-view +
  // flashed + inner control focused. `returnTo` rewires Back to the
  // PQR preview.
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const formContainerRef = useRef<HTMLDivElement>(null)
  useFieldFocusHandler({
    ready: true,
    resolveTarget: (params) => {
      if (!params.focusField) return null
      const root = formContainerRef.current ?? document
      const el = root.querySelector(
        `[data-focus-field="${params.focusField}"]`
      ) as HTMLElement | null
      if (!el) return null
      const inner = el.querySelector(
        'input, select, textarea, button, [role="button"]'
      ) as HTMLElement | null
      return inner ?? el
    }
  })
  const { getProject } = useProjects()
  const { getSection, updateSection } = useWpsSections()
  const { getLabelsMerged, addLabel } = useCatalog()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)

  const [postHeating, setPostHeating] = useState<string[]>([])
  const [pwht, setPwht] = useState<string[]>([])
  const [coolingMethod, setCoolingMethod] = useState<string[]>([])
  const [notes, setNotes] = useState('')
  const [cleaningNotes, setCleaningNotes] = useState(DEFAULT_CLEANING_GRINDING_NOTES)
  const [rootExternalTreatment, setRootExternalTreatment] = useState('')
  const [savedPostWeldJson, setSavedPostWeldJson] = useState('')
  const [saving, setSaving] = useState(false)

  const postWeldPayload = useMemo(
    () =>
      JSON.stringify({
        postHeating,
        pwht,
        coolingMethod,
        notes,
        cleaningNotes,
        rootExternalTreatment
      }),
    [postHeating, pwht, coolingMethod, notes, cleaningNotes, rootExternalTreatment]
  )

  const isDirty = useMemo(() => {
    if (!savedPostWeldJson) return postWeldPayload !== EMPTY_POSTWELD_PAYLOAD
    return postWeldPayload !== savedPostWeldJson
  }, [postWeldPayload, savedPostWeldJson])

  const postHeatingOptions = useMemo(
    () => getLabelsMerged('postweld_post_heating', postHeating),
    [getLabelsMerged, postHeating]
  )
  const pwhtOptions = useMemo(() => getLabelsMerged('postweld_pwht', pwht), [getLabelsMerged, pwht])
  const coolingOptions = useMemo(
    () => getLabelsMerged('postweld_cooling_method', coolingMethod),
    [getLabelsMerged, coolingMethod]
  )

  useEffect(() => {
    if (!pipeId) return
    const parsed = getSection('postWeld')
    if (parsed) {
      try {
        const next = {
          postHeating: parsed.postHeating || [],
          pwht: parsed.pwht || [],
          coolingMethod: parsed.coolingMethod || [],
          notes: typeof parsed.notes === 'string' ? parsed.notes : '',
          cleaningNotes: resolveCleaningNotes(parsed.cleaningNotes),
          rootExternalTreatment:
            typeof parsed.rootExternalTreatment === 'string' ? parsed.rootExternalTreatment : ''
        }
        setPostHeating(next.postHeating)
        setPwht(next.pwht)
        setCoolingMethod(next.coolingMethod)
        setNotes(next.notes)
        setCleaningNotes(next.cleaningNotes)
        setRootExternalTreatment(next.rootExternalTreatment)
        setSavedPostWeldJson(JSON.stringify(next))
      } catch (error) {
        // ignore
      }
    } else {
      const legacy = getSection('cleaningGrinding') as { cleaningNotes?: string } | null | undefined
      const legacyNotes = legacy?.cleaningNotes
      const cleaningFromLegacy =
        typeof legacyNotes === 'string' && legacyNotes.trim() ? legacyNotes : DEFAULT_CLEANING_GRINDING_NOTES
      const next = {
        postHeating: [] as string[],
        pwht: [] as string[],
        coolingMethod: [] as string[],
        notes: '',
        cleaningNotes: cleaningFromLegacy,
        rootExternalTreatment: ''
      }
      setPostHeating(next.postHeating)
      setPwht(next.pwht)
      setCoolingMethod(next.coolingMethod)
      setNotes(next.notes)
      setCleaningNotes(next.cleaningNotes)
      setRootExternalTreatment(next.rootExternalTreatment)
      setSavedPostWeldJson(JSON.stringify(next))
    }
  }, [pipeId, getSection])

  const handleSave = async () => {
    if (!pipeId || !isDirty || saving) return
    const body = {
      postHeating,
      pwht,
      coolingMethod,
      notes,
      cleaningNotes,
      rootExternalTreatment
    }
    setSaving(true)
    try {
      await updateSection('postWeld', body)
      setSavedPostWeldJson(JSON.stringify(body))
      toast.success('Post weld saved')
      navigate(-1)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save post weld')
    } finally {
      setSaving(false)
    }
  }

  if (!project || !pipeSet) {
    return (
      <Card title="Post Weld not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  const pageHeaderBackTo = returnTo
    ? { to: returnTo, label: backToLabelFor(returnTo) }
    : { onClick: () => navigate(-1), label: 'Back' }

  return (
    <div className="space-y-6" ref={formContainerRef}>
      <EditingReturnBanner returnTo={returnTo} contextLabel="Post Weld" />
      <PageHeader
        backTo={pageHeaderBackTo}
        title={
          <>
            <span>Post Weld Activities</span>
            <span className="text-heritage-grey/60">•</span>
            <span className="truncate text-text-grey">{project.name}</span>
          </>
        }
        subtitle={
          <>
            <span>{(project.endClients?.[0] ?? project.client)} • {project.location}</span>
            <Badge tone="primary">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </Badge>
          </>
        }
        actions={
          <>
            <Button disabled={!isDirty || saving} loading={saving} onClick={() => void handleSave()}>
              Save
            </Button>
          </>
        }
      />

      <div className="min-w-0 space-y-6 border-0 p-0">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-xs font-medium text-gray-500">Post Heating</label>
          <MultiSelect
            values={postHeating}
            options={postHeatingOptions}
            placeholder="Select post heating..."
            addLabel="Add Post Heating"
            onChange={setPostHeating}
            onCreateOption={async (value) => {
              await addLabel('postweld_post_heating', value)
              setPostHeating((prev) => (prev.includes(value) ? prev : [...prev, value]))
            }}
            className="mt-1"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500">PWHT</label>
          <MultiSelect
            values={pwht}
            options={pwhtOptions}
            placeholder="Select PWHT..."
            addLabel="Add PWHT Option"
            onChange={setPwht}
            onCreateOption={async (value) => {
              await addLabel('postweld_pwht', value)
              setPwht((prev) => (prev.includes(value) ? prev : [...prev, value]))
            }}
            className="mt-1"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-gray-500">Controlled Cooling Method</label>
          <MultiSelect
            values={coolingMethod}
            options={coolingOptions}
            placeholder="Select cooling method..."
            addLabel="Add Cooling Method"
            onChange={setCoolingMethod}
            onCreateOption={async (value) => {
              await addLabel('postweld_cooling_method', value)
              setCoolingMethod((prev) => (prev.includes(value) ? prev : [...prev, value]))
            }}
            className="mt-1"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-500">Notes</label>
        <textarea
          className="input-primary mt-1 h-24"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="e.g. Upon completion, and at a temperature below 419°F..."
        />
      </div>

      <div data-focus-field="rootExternalTreatment">
        <label className="text-xs font-medium text-gray-500">Root External Treatment</label>
        <input
          className="input-primary mt-1"
          value={rootExternalTreatment}
          onChange={(event) => setRootExternalTreatment(event.target.value)}
          placeholder="Enter treatment details..."
        />
      </div>

      <div data-focus-field="cleaningNotes">
        <label className="text-xs font-medium text-gray-500">Cleaning / Grinding</label>
        <textarea
          className="input-primary mt-1 h-28"
          value={cleaningNotes}
          onChange={(event) => setCleaningNotes(event.target.value)}
          placeholder="Optional edits to cleaning / grinding requirements..."
        />
      </div>
      </div>
    </div>
  )
}
